import React, { useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { clsx } from '@wego/alexis/helpers/clsx';

import Carousel from '@components/Carousel';

import { ENHANCED_HOTEL_IMAGES } from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import { countryCodeToFlagEmoji, postedDateDisplay } from '@helpers/hotel';
import { getReviewRatingTypeColorClassName } from '@helpers/review';

import Chevron from '@icons/chevron-right-small.svg';
import DisSatisfiedIcon from '@icons/sentiment_dissatisfied.svg';
import SatisfiedIcon from '@icons/sentiment_satisfied.svg';

import { getIsRtlState, getLocaleState, getTranslationsState } from '@redux/selectors';

import { HotelGuestReview } from '@wegoTypes/hotels/hotelGuestReview';
import { HotelGuestReviewContent } from '@wegoTypes/hotels/hotelGuestReviewContent';

import styles from './LatestReviews.module.scss';

export interface HotelLatestReviewsProps {
  reviews: HotelGuestReview;
  className?: string;
  showMoreReviews: () => void;
}

const LatestReviews: React.FC<HotelLatestReviewsProps> = ({
  reviews,
  className,
  showMoreReviews,
}) => {
  const { userCentricCoreConfigs } = useContext(GlobalContext);
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);
  const containerDivRef = useRef<HTMLDivElement>(null);

  const isEnhancedHotelImages =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === ENHANCED_HOTEL_IMAGES)?.value ===
    '1';

  const getReviewToShow = (
    reviewContents: HotelGuestReviewContent[],
  ): HotelGuestReviewContent[] => {
    const reviewToShow =
      reviewContents.find((content) => content.type === 'pros') ||
      reviewContents.find((content) => content.type === 'cons') ||
      reviewContents.find((content) => content.type === 'neutral');

    return reviewToShow ? [reviewToShow] : [];
  };

  const reviewCardWidth = useMemo<number>(() => {
    // 294 expected card width
    const cardWidth = containerDivRef.current?.clientWidth || 267;
    // 16 is card margin
    // 2 is card border
    return cardWidth + 16 + 2;
  }, [containerDivRef.current?.clientWidth]);

  return (
    <>
      {reviews?.data?.length ? (
        <div
          className={clsx(styles.container, className, isEnhancedHotelImages && styles.enhanced)}
        >
          <Carousel
            className={styles.latestReviews}
            isRtl={isRtl}
            itemLength={reviews?.data?.slice(0, 4)?.length}
            itemWidth={isEnhancedHotelImages ? reviewCardWidth - 3.75 : reviewCardWidth}
            scrollWidth={reviewCardWidth}
            isLightVersion={true}
          >
            {reviews?.data?.slice(0, 4).map((review, reviewIndex) => (
              <div ref={containerDivRef} key={reviewIndex} className={styles.reviewContainer}>
                <div className={styles.reviewHeader}>
                  <div className={styles.reviewInfo}>
                    <div
                      className={clsx(
                        styles.reviewScore,
                        getReviewRatingTypeColorClassName(review.rating * 10, styles),
                      )}
                    >
                      {parseFloat(String(review.rating)).toFixed(review.rating < 10 ? 1 : 0)}
                    </div>
                    <div className={styles.author}>
                      <div className={styles.authorName}>{review.author.name}</div>
                      {review.author.country ? (
                        <div className={styles.authorDetails}>
                          {countryCodeToFlagEmoji(review.author.country.code)}&nbsp;
                          <span className={styles.detail}>{review.author.country.name}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <small className={styles.reviewPostedDate}>
                    {postedDateDisplay(translations, new Date(review.postedAt), locale)}
                  </small>
                </div>

                <div className={styles.contentContainer}>
                  <>
                    {!!review.contents ? (
                      getReviewToShow(review.contents).map((content) => {
                        return (
                          <div
                            key={content.comment}
                            className={clsx(styles.comment, !review.title && styles.mgTop)}
                          >
                            {content.type === 'pros' ? (
                              <SatisfiedIcon className={styles.satisfied} />
                            ) : null}
                            {content.type === 'cons' ? (
                              <DisSatisfiedIcon className={styles.disSatisified} />
                            ) : null}

                            <p
                              className={clsx(
                                styles.text,
                                content.type === 'neutral' && styles.neutral,
                              )}
                            >
                              {content.comment}
                            </p>
                          </div>
                        );
                      })
                    ) : (
                      <small className={clsx(styles.comment, styles.noComment)}>
                        {translations.no_comment_review}
                      </small>
                    )}
                    {isEnhancedHotelImages ? (
                      <div className={styles.authorInfo}>
                        <div className={styles.authorName}>{review.author.name}</div>
                        {review.author.country ? (
                          <div className={styles.authorDetails}>
                            {countryCodeToFlagEmoji(review.author.country.code)}
                            <span className={styles.detail}>{review.author.country.name}</span>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                </div>
              </div>
            ))}
          </Carousel>
          <div className={styles.showAllReview} onClick={showMoreReviews}>
            {translations.view_more_reviews}
            <Chevron className={clsx(styles.chevron)} />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default LatestReviews;
